import { useState, useCallback, useMemo } from 'react'
import { NavLink } from 'react-router'
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { cn } from '@/lib/cn'
import { useAuth } from '@/lib/auth'
import { useMenuVisibility } from '@/lib/menu-visibility'
import { NAV_ITEMS, isSubGroup } from './nav-items'
import { filterNavItems } from './nav-visibility'
import styles from './Sidebar.module.css'

const SIDEBAR_KEY = 'fiket-admin-sidebar-open'

export function Sidebar() {
  const { user, logout } = useAuth()
  const { overrides } = useMenuVisibility()
  const [open, setOpen] = useState(() => localStorage.getItem(SIDEBAR_KEY) !== 'false')

  const toggle = useCallback(() => {
    setOpen(prev => {
      localStorage.setItem(SIDEBAR_KEY, String(!prev))
      return !prev
    })
  }, [])

  // dev 서버에서만 dev 도구 노출 + '메뉴 관리' 설정 반영
  const groups = useMemo(
    () => filterNavItems(NAV_ITEMS, { isDev: import.meta.env.DEV, overrides }),
    [overrides],
  )

  return (
    <aside className={cn(styles.root, !open && styles.collapsed)}>
      {/* 헤더: 로고 + 토글 */}
      <div className={styles.logo}>
        {open && (
          <>
            <span className={styles.logoText}>Fiket</span>
            <span className={styles.logoBadge}>Admin</span>
          </>
        )}
        <button className={styles.toggleBtn} onClick={toggle} title={open ? '사이드바 접기' : '사이드바 펼치기'}>
          {open ? <PanelLeftClose size={16} /> : <PanelLeftOpen size={16} />}
        </button>
      </div>
      <nav className={styles.nav}>
        {groups.map((group) => (
          <div key={group.group} className={styles.group}>
            {open && <span className={styles.groupLabel}>{group.group}</span>}
            {group.items.map((item) => {
              if (isSubGroup(item)) {
                return (
                  <div key={item.subgroup} className={styles.subgroup}>
                    {open && <span className={styles.subgroupLabel}>{item.subgroup}</span>}
                    {item.items.map((sub) => (
                      <NavLink
                        key={sub.to}
                        to={sub.to}
                        className={({ isActive }) => cn(styles.link, styles.subLink, isActive && styles.linkActive)}
                        title={!open ? sub.label : undefined}
                      >
                        <span className={styles.linkIcon}>{sub.icon}</span>
                        {open && sub.label}
                      </NavLink>
                    ))}
                  </div>
                )
              }
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => cn(styles.link, isActive && styles.linkActive)}
                  title={!open ? item.label : undefined}
                >
                  <span className={styles.linkIcon}>{item.icon}</span>
                  {open && item.label}
                </NavLink>
              )
            })}
          </div>
        ))}
      </nav>
      <div className={styles.footer}>
        {open && user && <span className={styles.userEmail}>{user.email}</span>}
        <button className={styles.logoutBtn} onClick={() => logout()} title={!open ? '로그아웃' : undefined}>
          {open ? '로그아웃' : '🚪'}
        </button>
      </div>
    </aside>
  )
}
